import { FastBadge } from "@/components/FastBadge";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, Search, MapPin, Star, Plus, Minus, ShoppingCart, BadgeCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Reveal } from "@/components/Extras";
import { api, inr, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PriceTag } from "@/components/PriceTag";

export const LAB_CART_KEY = "nrg_lab_cart";
let locPromise;
// one shared location request per page load
export function useVisitorLoc() {
  const [loc, setLoc] = useState(null);
  useEffect(() => {
    locPromise = locPromise || new Promise((res) => {
      if (!navigator.geolocation) return res(null);
      navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { timeout: 10000 });
    });
    locPromise.then((l) => l && setLoc(l));
  }, []);
  return loc;
}

export function readLabCart() {
  try { return JSON.parse(localStorage.getItem(LAB_CART_KEY)) || null; } catch { return null; }
}

function TestRow({ t, qty, onAdd }) {
  return (
    <div className="py-3 border-b border-[#E2E8ED] last:border-0" data-testid={`lab-offer-${t.id}`}>
      <p className="text-sm font-medium flex items-start gap-1 break-words">{t.partner.name}<BadgeCheck className="h-3.5 w-3.5 mt-0.5 text-[#086B2D] shrink-0" /></p>
      <p className="text-xs muted flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
        {t.partner.distance_km != null && <span className="inline-flex items-center gap-1 whitespace-nowrap"><MapPin className="h-3 w-3" />{t.partner.distance_km} km</span>}
        {t.partner.rating && <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-[#FF9700] text-[#FF9700]" />{t.partner.rating}</span>}
        <span>{t.partner.city}</span>
        <FastBadge show={t.partner.fast_reports} small testId={`lab-fast-${t.id}`} />
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm min-w-0"><PriceTag strike={t.mrp} offer={t.price} className="flex-wrap" testId={`lab-offer-price-${t.id}`} /></div>
        {qty ? <div className="flex items-center gap-1.5 shrink-0">
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => onAdd(t, -1)} aria-label="Less" data-testid={`lab-minus-${t.id}`}><Minus className="h-3.5 w-3.5" /></Button>
          <span className="w-4 text-center text-sm">{qty}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => onAdd(t, 1)} aria-label="More" data-testid={`lab-plus-${t.id}`}><Plus className="h-3.5 w-3.5" /></Button></div>
          : <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => onAdd(t, 1)} data-testid={`lab-add-${t.id}`}>Add</Button>}
      </div>
    </div>
  );
}

function CartBar({ cart, onCheckout }) {
  const { user } = useAuth();
  const items = Object.values(cart.items);
  const count = items.reduce((s, i) => s + i.qty, 0);
  const total = items.reduce((s, i) => s + i.qty * i.price, 0);
  return (
    <div className="fixed bottom-24 sm:bottom-6 left-4 right-[84px] sm:right-24 z-50 mx-auto max-w-2xl rounded-2xl bg-[#17232E] text-white p-4 flex items-center justify-between gap-3 shadow-2xl fade-up" data-testid="lab-cart-bar">
      <span className="text-sm flex items-center gap-2 min-w-0"><ShoppingCart className="h-4 w-4 shrink-0" /><span className="truncate">{count} test{count > 1 ? "s" : ""} · {cart.partner_name} · {inr(total)}</span></span>
      <Button className="rounded-full bg-[#086B2D] hover:bg-[#075a26] shrink-0" onClick={onCheckout} data-testid="lab-cart-checkout">{user?.role === "patient" ? "Book now" : "Sign in & book"}</Button>
    </div>
  );
}

export function LabBooking() {
  const loc = useVisitorLoc();
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [tests, setTests] = useState(null);
  const [cart, setCart] = useState(() => readLabCart() || { partner_id: null, partner_name: "", items: {} });
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => api.get("/public/lab-tests", { params: { q: q || undefined, ...(loc || {}) } }).then((r) => live && setTests(r.data)).catch(() => live && setTests([])), 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, loc]);
  useEffect(() => { Object.keys(cart.items).length ? localStorage.setItem(LAB_CART_KEY, JSON.stringify(cart)) : localStorage.removeItem(LAB_CART_KEY); }, [cart]);
  const groups = useMemo(() => {
    const g = {};
    (tests || []).forEach((t) => { (g[t.title.trim().toLowerCase()] ||= { title: t.title, offers: [] }).offers.push(t); });
    return Object.values(g).map((x) => ({ ...x, offers: x.offers.sort((a, b) => a.price - b.price || (a.partner.distance_km ?? 1e9) - (b.partner.distance_km ?? 1e9)) })).slice(0, 12);
  }, [tests]);
  const add = (t, d) => {
    if (cart.partner_id && cart.partner_id !== t.partner.id && d > 0) {
      if (!window.confirm(`Your cart has tests from ${cart.partner_name}. One lab per booking – start a new cart with ${t.partner.name}?`)) return;
      return setCart({ partner_id: t.partner.id, partner_name: t.partner.name, items: { [t.id]: { qty: 1, price: t.price } } });
    }
    setCart((c) => {
      const qty = Math.max(0, Math.min(20, (c.items[t.id]?.qty || 0) + d));
      const items = { ...c.items, [t.id]: { qty, price: t.price } };
      if (!qty) delete items[t.id];
      return Object.keys(items).length ? { partner_id: t.partner.id, partner_name: t.partner.name, items } : { partner_id: null, partner_name: "", items: {} };
    });
  };
  const checkout = () => { if (!user) toast.message("Sign in to complete your booking – your cart is saved"); nav(`/patient/suvidha/partner/${cart.partner_id}?checkout=1`); };
  const qtyOf = (t) => (cart.partner_id === t.partner.id ? cart.items[t.id]?.qty : 0);
  return (
    <section id="lab-tests" className="mx-auto max-w-7xl px-5 sm:px-8 py-20" data-testid="lab-booking-section">
      <div className="grid lg:grid-cols-12 gap-8 items-end">
        <Reveal className="lg:col-span-7"><p className="eyebrow">Book a lab test</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight">Compare prices across verified labs. <span className="text-[#086477]">Free home collection.</span></h2>
          <p className="mt-3 muted max-w-xl">Search a test, pick the lab that suits you and add it to your cart – sign in only when you're ready to book.</p></Reveal>
        <Reveal className="lg:col-span-5" delay={100}><div className="relative"><Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 muted" />
          <Input className="h-12 pl-11 rounded-full" placeholder="Search tests – CBC, thyroid, sugar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lab-test-search" /></div></Reveal>
      </div>
      <div className="mt-10">
        {tests === null ? <Loader2 className="h-6 w-6 animate-spin text-[#086477]" data-testid="lab-tests-loading" /> : groups.length === 0 ? (
          <div className="surface p-8 text-center" data-testid="lab-tests-empty"><FlaskConical className="h-8 w-8 mx-auto text-[#086477]" /><p className="mt-3 font-medium">{q ? "No labs offer this test yet" : "Labs are onboarding"}</p><p className="text-sm muted mt-1">Check back soon or try another test name.</p></div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{groups.map((g, i) => (
            <Reveal key={g.title} delay={(i % 6) * 60}>
              <div className="surface p-5 h-full" data-testid={`lab-test-card-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-2"><p className="font-semibold break-words min-w-0">{g.title}</p><span className="text-xs rounded-full bg-[#086477]/10 text-[#086477] px-2.5 py-1 whitespace-nowrap">{g.offers.length} lab{g.offers.length > 1 ? "s" : ""} · from {inr(g.offers[0].price)}</span></div>
                <div className="mt-2">{g.offers.slice(0, 4).map((t) => <TestRow key={t.id} t={t} qty={qtyOf(t)} onAdd={add} />)}</div>
              </div>
            </Reveal>))}</div>)}
      </div>
      {Object.keys(cart.items).length > 0 && <CartBar cart={cart} onCheckout={checkout} />}
    </section>
  );
}

export function PartnerLogoStrip() {
  const [logos, setLogos] = useState([]);
  useEffect(() => { api.get("/public/partner-logos").then((r) => setLogos(r.data)).catch(() => {}); }, []);
  if (logos.length < 4) return null;
  const loop = [...logos, ...logos];
  return (
    <div className="border-y border-[#E2E8ED] bg-white py-8 overflow-hidden" data-testid="partner-logo-strip">
      <p className="eyebrow text-center mb-5">Verified Suvidha Centre partners</p>
      <div className="[mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
        <div className="flex gap-10 w-max marquee items-center">{loop.map((p, i) => (
          <div key={`${p.id}-${i}`} className="flex items-center gap-3 shrink-0" data-testid={i < logos.length ? `partner-logo-${p.id}` : undefined}>
            <img src={fileUrl(p.logo_url)} alt={p.name} className="h-12 w-12 rounded-xl object-cover border border-[#E2E8ED]" loading="lazy" />
            <span className="text-sm font-medium text-[#17232E]/80 whitespace-nowrap">{p.name}</span>
          </div>))}</div>
      </div>
    </div>
  );
}
